// Форматирование цен, дат и длительностей (русские строки — их переводит i18n в DOM)

export interface Formatter {
  format(n: number): string;
}

// Сумы и тенге пишем по-местному: «1 226 700 сум», «45 000 ₸» — короче и привычнее, чем «UZS»/«KZT»
const LOCAL_SIGN: Record<string, string> = { uzs: 'сум', kzt: '₸' };

export function money(currency?: string | null): Formatter {
  const cur = String(currency || 'usd').toLowerCase();
  const sign = LOCAL_SIGN[cur];
  if (sign) {
    const num = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
    return { format: (n) => `${num.format(n)} ${sign}` };
  }
  try {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: cur.toUpperCase(), maximumFractionDigits: 0 });
  } catch {
    return { format: (n) => `${Math.round(n)} ${cur.toUpperCase()}` };
  }
}

// Короткая запись больших сумм для узких ячеек: «2,2 млн сум»
export function moneyShort(currency?: string | null): Formatter {
  const full = money(currency);
  const cur = String(currency || 'usd').toLowerCase();
  let compact: Formatter;
  try {
    const sign = LOCAL_SIGN[cur];
    if (sign) {
      const num = new Intl.NumberFormat('ru-RU', { notation: 'compact', maximumFractionDigits: 1 });
      compact = { format: (n) => `${num.format(n)} ${sign}` };
    } else {
      compact = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: cur.toUpperCase(), notation: 'compact', maximumFractionDigits: 1 });
    }
  } catch {
    compact = full;
  }
  return { format: (n) => (n >= 100000 ? compact.format(n) : full.format(n)) };
}

// Очень короткая цена для узких клеток календаря: 29 650 → «29,7к», 1 226 700 → «1,2м»
export function tinyPrice(n: number): string {
  const f = (x: number) => x.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
  return n >= 1e6 ? `${f(n / 1e6)}м` : n >= 1e4 ? `${f(n / 1e3)}к` : String(Math.round(n));
}

// Уровень цены 0–4 внутри набора (0 — самые дешёвые 20%); null — уровень не показываем
export function priceLevels(prices: number[]): (p: number) => number | null {
  const sorted = [...prices].sort((a, b) => a - b);
  // Меньше трёх цен или все одинаковые — уровень не показываем
  if (sorted.length < 3 || sorted[0] === sorted[sorted.length - 1]) return () => null;
  return (p) => Math.min(4, Math.floor((sorted.findIndex((x) => x >= p) / sorted.length) * 5));
}

// ---------- Даты ----------

export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Сегодня, YYYY-MM-DD (по часам браузера) */
export const today = (): string => isoDate(new Date());

export function addDays(d: Date | string | number, n: number): Date {
  const r = typeof d === 'string' && d.length === 10 ? new Date(`${d}T12:00:00`) : new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Дата YYYY-MM-DD через n дней */
export const addDaysIso = (day: string, n: number): string => isoDate(addDays(day, n));

export function daysBetween(a: string, b: string): number {
  return Math.round((new Date(`${b.slice(0, 10)}T12:00:00`).getTime() - new Date(`${a.slice(0, 10)}T12:00:00`).getTime()) / 86400000);
}

export function shiftMonth(month: string, step: number): string {
  const d = new Date(`${month}-01T12:00:00`);
  d.setMonth(d.getMonth() + step);
  return isoDate(d).slice(0, 7);
}

export function formatDay(day: string, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', weekday: 'short' }): string {
  return new Date(`${day.slice(0, 10)}T12:00:00`).toLocaleDateString('ru-RU', opts);
}

export function formatMonth(month: string, opts: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' }): string {
  return new Date(`${month}-01T12:00:00`).toLocaleDateString('ru-RU', opts);
}

// "2026-11-15" → "15 ноября", "2026-11" → "ноябрь 2026"
export function formatDateOrMonth(s: string): string {
  return s.length === 7 ? formatMonth(s) : formatDay(s, { day: 'numeric', month: 'long' });
}

export function formatDuration(min?: number | null): string {
  if (!min) return '';
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h} ч${m ? ` ${m} мин` : ''}` : `${m} мин`;
}

export function formatAgo(iso?: string | null): string {
  if (!iso) return 'ещё не проверялось';
  const min = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;
  return formatDay(iso, { day: 'numeric', month: 'short' });
}

// «найдено 3 ч назад» по ISO-времени
export function ageText(iso: string): string {
  const h = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 3600000));
  if (h < 1) return 'меньше часа назад';
  if (h < 24) return `${h} ${plural(h, 'час', 'часа', 'часов')} назад`;
  const d = Math.round(h / 24);
  return `${d} ${plural(d, 'день', 'дня', 'дней')} назад`;
}

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

/** Экранирование для HTML-строк (окно печати PDF) */
export function esc(s: unknown): string {
  return String(s ?? '').replace(/[&"'<>]/g, (c) => ({ '&': '&amp;', '"': '&quot;', "'": '&#39;', '<': '&lt;', '>': '&gt;' }[c] as string));
}

/** Первая буква заглавная */
export const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
